<script setup lang="ts">
import axios from 'axios'
import { Search, Store } from 'lucide-vue-next'
import { useDebounceFn } from '@vueuse/core'

import useNotification from '@/composables/notification'
import type { Partner, PartnerProduct } from '@/types/types'

interface Props {
  isOpen: boolean
  partner: Partner | null
  associatedProducts: PartnerProduct[]
}

interface Emits {
  (e: 'close'): void
  (e: 'select', product: PartnerProduct): void
  (e: 'remove', product: PartnerProduct): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { t } = useI18n()
const notification = useNotification()
const { hasPermissionBySlug } = useAccess()

const fetchPartnerProducts = async (partnerId: number, accountId: number): Promise<PartnerProduct[]> => {
  try {
    const { data } = await axios.get(`/partners/${partnerId}/items`, {
      params: {
        accountId,
        search: searchQuery.value,
        ...(props.partner?.features?.groupedItems ? { grouped: 1 } : {})
      }
    })
    return data.data || []
  } catch (error) {
    const status = error.response?.status
    // 401 is handled globally by the axios interceptor (partner reconnection toast).
    if (status === 401) return []
    if (status === 403) {
      notification.create('error', {
        text: error.response.data?.error,
        persist: true
      })
    } else {
      notification.create('error', {
        text: error?.message || t('pages.partner.errors.fetchProducts'),
        timeout: 5000
      })
    }

    return []
  }
}

const searchQuery = ref('')
const products = ref<PartnerProduct[]>([])
const isLoading = ref(false)
const partnerAccounts = ref<any[]>([])
const selectedAccountId = ref<number | null>(null)

const productsPerPage = ref(10)
const currentPage = ref(1)
const currentTab = ref('product')

const hoveredProductToRemove = ref<string | null>(null)

const tabs = [
  {
    value: 'product',
    name: t('pages.reward.products.type.product')
  },
  {
    value: 'menu',
    name: t('pages.reward.products.type.menu')
  }
]

const productsColumns = computed(() => ({
  name: {
    label:
      currentTab.value === 'product' ? t('pages.reward.products.productName') : t('pages.reward.products.menuName'),
    headerClass: 'w-auto! text-left'
  },
  sku: {
    label: t('pages.reward.productAssociation.sku'),
    headerClass: 'w-32! text-left'
  },
  options: {
    label: '',
    headerClass: 'w-[125px]!',
    align: 'right' as const
  }
}))

const productsFormattedForTable = computed(() => {
  return products.value
    .map((product, index) => {
      // Back returns 2 shapes:
      //  - flat   : { id, name, type }              (default)
      //  - grouped: { name, sku, type, itemIds[], storeCount } (Lightspeed via ?grouped=1)
      const grouped = Array.isArray((product as any).itemIds)
      const externalIds: string[] = grouped
        ? ((product as any).itemIds as Array<string | number>).map(String)
        : [String(product.id)]
      const sku = grouped ? String((product as any).sku ?? '') : String(product.id ?? '')
      return {
        id: index,
        data: {
          name: product.name,
          sku,
          type: product.type,
          externalIds,
          storeCount: grouped ? Number((product as any).storeCount ?? externalIds.length) : 1
        }
      }
    })
    .filter((item) => item.data.type === currentTab.value)
})

const getAssociatedCount = (externalIds: string[]): number =>
  externalIds.filter((eid) => props.associatedProducts.some((p) => p.externalId.toString() === eid)).length

const isAllAssociated = (externalIds: string[]) =>
  externalIds.length > 0 && getAssociatedCount(externalIds) === externalIds.length

const isPartiallyAssociated = (externalIds: string[]) => {
  const count = getAssociatedCount(externalIds)
  return count > 0 && count < externalIds.length
}

const getRowButtonVariant = (data: { sku: string; externalIds: string[] }) => {
  if (hoveredProductToRemove.value === data.sku) return 'outline-destructive'
  if (isAllAssociated(data.externalIds)) return 'outline-success'
  if (isPartiallyAssociated(data.externalIds)) return 'outline-warning'
  return 'outline'
}

const pagination = computed(() => {
  const total = productsFormattedForTable.value.length
  const lastPage = Math.max(1, Math.ceil(total / productsPerPage.value))
  const currentPageNum = Math.max(1, Math.min(currentPage.value, lastPage))

  return {
    currentPage: currentPageNum,
    lastPage,
    total,
    pageSize: productsPerPage.value
  }
})

// Sequence guard: only the latest started fetch commits its result. Without it, the
// `onMounted` empty-search load (or a slower in-flight request for a stale query)
// could resolve last and overwrite the results matching what the user has just typed.
let fetchSeq = 0
const runFetch = async () => {
  if (!props.partner || selectedAccountId.value == null) return
  const seq = ++fetchSeq
  isLoading.value = true
  try {
    const result = await fetchPartnerProducts(props.partner.id, selectedAccountId.value)
    if (seq !== fetchSeq) return
    products.value = result
    currentPage.value = 1
  } finally {
    if (seq === fetchSeq) isLoading.value = false
  }
}

const debouncedFetchProducts = useDebounceFn(runFetch, 300)

watch(searchQuery, () => debouncedFetchProducts())

const currenParterSlug = computed(() => {
  return props.partner?.slug
})

const disabledMenu = computed(() => {
  return currenParterSlug.value === 'laddition' && currentTab.value === 'menu'
})

const changeTab = () => {
  currentPage.value = 1
}

const handleSearch = (ev: Event) => {
  const query = (ev.target as HTMLInputElement).value
  searchQuery.value = query
}

const handleSelectRow = (row: { name: string; type: 'product' | 'menu'; externalIds: string[] }) => {
  // Grouped mode (Lightspeed): one row carries N store-specific itemIds. A single click
  // attaches every store variant at once via N successive `select` emits — the parent
  // dedupes (addProductIfNotExists). Same logic for `remove` when all variants are
  // already associated. Flat mode is the same code path with externalIds.length === 1.
  const allAssociated = isAllAssociated(row.externalIds)
  const partnerId = props.partner?.id || 0

  if (allAssociated) {
    hoveredProductToRemove.value = null
    row.externalIds.forEach((eid) => {
      emit('remove', { externalId: eid, name: row.name, type: row.type, partnerId })
    })
  } else {
    row.externalIds.forEach((eid) => {
      const alreadyIn = props.associatedProducts.some((p) => p.externalId.toString() === eid)
      if (!alreadyIn) {
        emit('select', { externalId: eid, name: row.name, type: row.type, partnerId })
      }
    })
  }
}

const handleClose = () => {
  searchQuery.value = ''
  products.value = []
  partnerAccounts.value = []
  selectedAccountId.value = null
  currentPage.value = 1
  currentTab.value = 'product'
  emit('close')
}

const loadPartnerAccounts = async () => {
  if (!props.partner) return

  try {
    if (hasPermissionBySlug('marketplace.read')) {
      const { data } = await axios.get('/partner-parameters', {
        params: {
          hasRewardSupport: 1,
          isActive: 1,
          partnerId: props.partner.id
        }
      })
      partnerAccounts.value = data.data

      if (partnerAccounts.value.length > 0) {
        selectedAccountId.value = partnerAccounts.value[0].id
      }
    }
  } catch (error) {
    console.error('Error loading partner accounts:', error)
    notification.create('error', {
      text: t('pages.partner.errors.fetchAccounts')
    })
  }
}

const handleAccountChange = async (accountId: number) => {
  selectedAccountId.value = accountId
  await runFetch()
}

const handleRemoveMouseOver = (rowKey: string, externalIds: string[]) => {
  if (isAllAssociated(externalIds)) {
    hoveredProductToRemove.value = rowKey
  }
}

const handleRemoveMouseLeave = () => {
  hoveredProductToRemove.value = null
}

const changePage = (page: number) => {
  currentPage.value = page
}

onMounted(async () => {
  await loadPartnerAccounts()
  await runFetch()
})
</script>

<template>
  <BaseModal
    :is-open="isOpen"
    :heading-title="t('pages.reward.productAssociation.modalTitle', { partnerName: partner?.name || '' })"
    size="5xl"
    :cancel="t('global.actions.cancel')"
    class="h-full"
    @close="handleClose"
  >
    <div>
      <div v-if="partnerAccounts.length > 1" class="mb-4">
        <label class="mb-2 block text-sm font-medium text-foreground">
          {{ t('pages.reward.productAssociation.selectAccount') }}
        </label>
        <ComboboxSelect
          value-key="id"
          label-key="text"
          :model-value="selectedAccountId"
          :options="
            partnerAccounts.map((account) => ({
              id: account.id,
              text:
                account.name ||
                account.external_id ||
                t('pages.reward.productAssociation.account', { id: account.publicId }),
              label:
                account.name ||
                account.external_id ||
                t('pages.reward.productAssociation.account', { id: account.publicId })
            }))
          "
          trigger-class="w-full"
          @update:model-value="handleAccountChange"
        />
      </div>

      <BaseTab v-model="currentTab" class="mb-4" :tabs="tabs" @update:model-value="changeTab" />

      <BaseTip
        v-if="disabledMenu"
        severity="info"
        size="small"
        :text="t('pages.loyaltyPage.edit.menuDisabled')"
        class="mb-4"
      />

      <!-- Search input lifted out of the AppTable header so it stays mounted across
           loading/empty/results states (the user can keep typing without the input
           being unmounted when isLoading flips). -->
      <div class="relative w-full mb-4 sm:max-w-sm">
        <Search class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref="searchFieldRef"
          v-model="searchQuery"
          type="search"
          class="pl-10"
          :placeholder="t('pages.customer.audiences.search.placeholder')"
          @input="handleSearch"
        />
      </div>

      <div v-if="isLoading" class="space-y-4">
        <div v-for="i in 3" :key="`skeleton-${i}`" class="border border-border rounded-lg p-4">
          <div class="flex items-center gap-3">
            <div class="w-12 h-12 bg-stone-200 rounded-lg animate-pulse"></div>
            <div class="flex-1">
              <div class="h-4 bg-stone-200 rounded w-32 animate-pulse mb-2"></div>
              <div class="h-3 bg-stone-200 rounded w-24 animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>

      <AppTable
        v-else-if="products.length > 0"
        :rows="
          productsFormattedForTable.slice(
            (pagination.currentPage - 1) * productsPerPage,
            pagination.currentPage * productsPerPage
          )
        "
        :columns="productsColumns"
        :pagination="productsFormattedForTable.length > 0 ? pagination : null"
        @update:change-page="changePage"
      >
        <template #title>
          <CardTitle>{{ currentTab === 'menu' ? 'Mes menus' : 'Mes produits' }}</CardTitle>
        </template>
        <template #empty-state>
          <BaseIcon
            :name="searchQuery ? 'HeroMagnifyingGlassIconOutline' : 'HeroGiftIconOutline'"
            class="w-8 h-8 text-muted-foreground mx-auto mb-2"
          />
          <p class="text-sm text-muted-foreground">
            <template v-if="searchQuery">
              {{
                currentTab === 'product'
                  ? t('pages.reward.productAssociation.noProductFound', { searchTerm: searchQuery })
                  : t('pages.reward.productAssociation.noMenuFound', { searchTerm: searchQuery })
              }}
            </template>
            <template v-else>
              {{ t('pages.reward.productAssociation.noProductAvailable') }}
            </template>
          </p>
        </template>
        <template #td-name="{ data }">
          <div class="min-w-0">
            <h4 class="font-medium text-foreground truncate">{{ data.name }}</h4>
            <p v-if="data.description" class="text-xs text-muted-foreground truncate">
              {{ data.description }}
            </p>
          </div>
        </template>

        <template #td-sku="{ data }">
          <div class="flex items-center gap-2">
            <span class="font-mono text-sm text-muted-foreground">{{ data.sku }}</span>
            <BaseChip
              v-if="data.storeCount > 1"
              :text="t('components.productAssociation.storeCount', { count: data.storeCount }, data.storeCount)"
            >
              <template #leading>
                <Store />
              </template>
            </BaseChip>
          </div>
        </template>

        <template #td-options="{ data }">
          <div class="text-right">
            <Button
              :variant="getRowButtonVariant(data)"
              size="sm"
              class="w-full"
              :disabled="disabledMenu"
              @mouseenter="() => handleRemoveMouseOver(data.sku, data.externalIds)"
              @mouseleave="handleRemoveMouseLeave"
              @click.stop="
                () => {
                  handleSelectRow({
                    name: data.name,
                    type: data.type,
                    externalIds: data.externalIds
                  })
                }
              "
            >
              <span v-if="hoveredProductToRemove === data.sku">
                {{ t('pages.reward.productAssociation.removeProduct') }}
              </span>

              <span v-else-if="isAllAssociated(data.externalIds)">
                {{ t('pages.reward.productAssociation.selectedProduct') }}
              </span>

              <span v-else-if="isPartiallyAssociated(data.externalIds)">
                {{ getAssociatedCount(data.externalIds) }} / {{ data.externalIds.length }}
                {{ t('pages.reward.productAssociation.associateProduct') }}
              </span>

              <span v-else>
                {{ t('pages.reward.productAssociation.associateProduct') }}
              </span>
            </Button>
          </div>
        </template>
      </AppTable>

      <div v-else-if="searchQuery" class="text-center py-8">
        <BaseIcon name="HeroMagnifyingGlassIconOutline" class="w-8 h-8 text-muted-foreground mx-auto mb-2" />
        <p class="text-sm text-muted-foreground">
          {{
            currentTab === 'product'
              ? t('pages.reward.productAssociation.noProductFound', { searchTerm: searchQuery })
              : t('pages.reward.productAssociation.noMenuFound', { searchTerm: searchQuery })
          }}
        </p>
      </div>

      <div v-else class="text-center py-8">
        <BaseIcon name="HeroGiftIconOutline" class="w-8 h-8 text-muted-foreground mx-auto mb-2" />
        <p class="text-sm text-muted-foreground">{{ t('pages.reward.productAssociation.noProductAvailable') }}</p>
      </div>
    </div>
    <template #footer>
      <div class="flex justify-end gap-3">
        <Button variant="outline" @click="handleClose">
          {{ t('global.actions.cancel') }}
        </Button>
      </div>
    </template>
  </BaseModal>
</template>
